import { usePresignedURLLabel } from '@/hooks'
import CopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import { Base, toast } from '@itsrever/design-system'
import {
  PbLogisticsSummary,
  PbLogisticsSummaryReturnMethodEnum,
  PbLogisticsSummaryStatusEnum,
  PbLogisticsSummaryWarehouse
} from '@itsrever/rever-api'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

const {
  Card,
  Button,
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverPositioner,
  PopoverPopup,
  PopoverArrow
} = Base

interface LogisticsIslandProps {
  processLogistics: PbLogisticsSummary
}

export const LogisticsIsland = ({ processLogistics }: LogisticsIslandProps) => {
  const { t } = useTranslation()

  return (
    <Card className="h-fit p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={20}
            height={20}
            stroke="currentColor"
            fill="none"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary-main"
            aria-hidden
          >
            <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
            <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
            <path d="M5 17h-2v-4m-1 -8h11v12m-4 0h6m4 0h2v-6h-8m0 -5h5l3 5"></path>
            <path d="M3 9l4 0"></path>
          </svg>
          <p className="text-sm font-semibold">{t('summary.logistics')}</p>
        </div>
        <DownloadLabelButton
          trackingUrl={processLogistics.tracking_url}
          trackingId={processLogistics.tracking_id}
        />
      </div>
      <dl className="space-y-2.5 font-roobert">
        <div className="space-y-1.5">
          <dt className="flex items-center gap-2 text-sm font-medium">
            <span className="shrink-0">
              <span className="uppercase">{processLogistics.carrier}</span>{' '}
              {t('logistics_island.tracking_label')}
            </span>
            <ShippingStatusLabel
              status={processLogistics.status}
              returnMethod={processLogistics.return_method}
              hasTrackingUrl={!!processLogistics.tracking_url}
            />
          </dt>
          <dd className="text-sm font-normal text-neutral-700">
            {processLogistics.tracking_url ? (
              <a
                href={processLogistics.tracking_url}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-4"
              >
                {processLogistics.tracking_id}
              </a>
            ) : (
              t('logistics_island.not_available')
            )}
          </dd>
        </div>
        {processLogistics.pickup_reference ? (
          <div className="space-y-1">
            <dt className="text-sm font-medium">
              <span className="uppercase">{processLogistics.carrier}</span>{' '}
              {t('logistics_island.pickup_label')}
            </dt>
            <dd className="flex items-center gap-2 text-sm font-normal text-neutral-700">
              <span>{processLogistics.pickup_reference}</span>
              <CopyButton value={processLogistics.pickup_reference} />
            </dd>
          </div>
        ) : null}
        <div className="space-y-1">
          <dt className="text-sm font-medium">
            {t('logistics_island.warehouse')}
          </dt>
          <dd className="text-sm font-normal text-neutral-700">
            {processLogistics.warehouse ? (
              <WarehousePopover warehouse={processLogistics.warehouse} />
            ) : (
              '-'
            )}
          </dd>
        </div>
      </dl>
    </Card>
  )
}

interface WarehousePopoverProps {
  warehouse: PbLogisticsSummaryWarehouse
}

const WarehousePopover = ({ warehouse }: WarehousePopoverProps) => {
  const { t } = useTranslation()

  const addressLines = [
    warehouse.address_line1,
    warehouse.address_line2,
    [warehouse.postcode, warehouse.city].filter(Boolean).join(' '),
    [warehouse.state_province, warehouse.country].filter(Boolean).join(', ')
  ].filter((line) => line && line.trim().length > 0)

  return (
    <PopoverRoot>
      <PopoverTrigger
        render={
          <button
            type="button"
            className="rounded text-left underline decoration-dotted underline-offset-4 hover:text-neutral-900"
          />
        }
      >
        {`${warehouse.first_name} - ${warehouse.company}`}
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverPositioner sideOffset={8} align="start">
          <PopoverPopup className="w-72 rounded-lg border border-neutral-200 bg-white p-4 font-roobert text-sm shadow-lg">
            <PopoverArrow />
            <dl className="space-y-3">
              {warehouse.first_name ? (
                <div>
                  <dt className="text-xs font-medium text-neutral-800">
                    {t('logistics_island.warehouse_popover.contact')}
                  </dt>
                  <dd className="text-neutral-800">{warehouse.first_name}</dd>
                </div>
              ) : null}
              {warehouse.company ? (
                <div>
                  <dt className="text-xs font-medium text-neutral-800">
                    {t('logistics_island.warehouse_popover.company')}
                  </dt>
                  <dd className="text-neutral-800">{warehouse.company}</dd>
                </div>
              ) : null}
              {warehouse.phone ? (
                <div>
                  <dt className="text-xs font-medium text-neutral-800">
                    {t('logistics_island.warehouse_popover.phone')}
                  </dt>
                  <dd className="text-neutral-800">
                    <a
                      href={`tel:${warehouse.phone}`}
                      className="underline underline-offset-4"
                    >
                      {warehouse.phone}
                    </a>
                  </dd>
                </div>
              ) : null}
              {warehouse.email ? (
                <div>
                  <dt className="text-xs font-medium text-neutral-800">
                    {t('logistics_island.warehouse_popover.email')}
                  </dt>
                  <dd className="text-neutral-800">
                    <a
                      href={`mailto:${warehouse.email}`}
                      className="underline underline-offset-4"
                    >
                      {warehouse.email}
                    </a>
                  </dd>
                </div>
              ) : null}
              {addressLines.length > 0 ? (
                <div>
                  <dt className="text-xs font-medium text-neutral-800">
                    {t('logistics_island.warehouse_popover.address')}
                  </dt>
                  <dd className="text-neutral-800">
                    {addressLines.map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </dd>
                </div>
              ) : null}
            </dl>
          </PopoverPopup>
        </PopoverPositioner>
      </PopoverPortal>
    </PopoverRoot>
  )
}

interface CopyButtonProps {
  value: string
}

const CopyButton = ({ value }: CopyButtonProps) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setCopied(false), 1500)
    } catch {
      return
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('summary.copied')}
      className="relative inline-flex items-center text-neutral-500 transition-colors before:absolute before:inset-0 before:block before:scale-150 hover:text-neutral-800"
    >
      {copied ? (
        <CheckIcon sx={{ fontSize: 14 }} />
      ) : (
        <CopyIcon sx={{ fontSize: 14 }} />
      )}
    </button>
  )
}

const BADGE_BASE_CLASS =
  'inline-block truncate rounded-full px-2 py-0.5 align-middle text-xs font-medium'

interface ShippingStatusLabelProps {
  status?: PbLogisticsSummaryStatusEnum
  returnMethod?: PbLogisticsSummaryReturnMethodEnum
  hasTrackingUrl: boolean
}

const getBadgeMeta = (
  status: PbLogisticsSummaryStatusEnum | undefined,
  returnMethod: PbLogisticsSummaryReturnMethodEnum | undefined,
  hasTrackingUrl: boolean
): { className: string; i18nKey: string } | null => {
  switch (status) {
    case PbLogisticsSummaryStatusEnum.Created:
      switch (returnMethod) {
        case PbLogisticsSummaryReturnMethodEnum.PickUp:
        case PbLogisticsSummaryReturnMethodEnum.DeliveryPickup:
          return {
            className: 'bg-[#d5e2ff] text-[#122368]',
            i18nKey: 'shipping_status.AWAITING_PICKUP'
          }
        case PbLogisticsSummaryReturnMethodEnum.DropOff:
          return {
            className: 'bg-[#d5e2ff] text-[#122368]',
            i18nKey: 'shipping_status.AWAITING_DROPOFF'
          }
        default:
          return {
            className: 'bg-[#d5e2ff] text-[#122368]',
            i18nKey: 'shipping_status.CREATED'
          }
      }
    case PbLogisticsSummaryStatusEnum.Collected:
      return {
        className: 'bg-[#c0eaff] text-[#124b68]',
        i18nKey: 'shipping_status.COLLECTED'
      }
    case PbLogisticsSummaryStatusEnum.InWarehouse:
      return {
        className: 'bg-[#c2f5da] text-[#0b4627]',
        i18nKey: 'shipping_status.IN_WAREHOUSE'
      }
    case PbLogisticsSummaryStatusEnum.Canceled:
      return {
        className: 'bg-[#ffecc0] text-[#624c18]',
        i18nKey: 'shipping_status.CANCELED'
      }
    case PbLogisticsSummaryStatusEnum.NoShippingStatus:
      // A tracking URL can outlive a cleared label, so neither "pending" nor "created" is certain.
      if (hasTrackingUrl) return null
      return {
        className: 'bg-[#ffd9c0]/70 text-[#683412]',
        i18nKey: 'shipping_status.NO_SHIPPING_STATUS'
      }
    default:
      // The API sends a shipping ERROR as no status; showing nothing beats a false "Label pending".
      return null
  }
}

const ShippingStatusLabel = ({
  status,
  returnMethod,
  hasTrackingUrl
}: ShippingStatusLabelProps) => {
  const { t } = useTranslation()
  const meta = getBadgeMeta(status, returnMethod, hasTrackingUrl)
  if (!meta) return null

  const label = t(meta.i18nKey)

  return (
    <span className={`${BADGE_BASE_CLASS} ${meta.className}`} title={label}>
      {label}
    </span>
  )
}

interface DownloadLabelButtonProps {
  trackingUrl?: string
  trackingId?: string
}

const DownloadLabelButton = ({
  trackingUrl,
  trackingId
}: DownloadLabelButtonProps) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { mutate } = usePresignedURLLabel()

  const handleDownloadLabel = () => {
    if (!id) return

    mutate(
      { processId: id },
      {
        onSuccess: (data) => {
          window.open(data.url, '_blank')
        },
        onError: () => {
          toast({
            variant: 'error',
            text: t('process_details_page.download_label_error')
          })
        }
      }
    )
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      // Some carriers (e.g. BigBlue) give a tracking id and a label but no tracking URL.
      disabled={!trackingUrl && !trackingId}
      onClick={handleDownloadLabel}
    >
      <FileDownloadIcon sx={{ fontSize: 16 }} />
      {t('process_details_page.download_label')}
    </Button>
  )
}
